import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ORG_REF, PERSON_REF, WEBSITE_REF } from "@/data/entity";
import { SEOWrapper } from "@/components/SEOWrapper";
import { SiteShell } from "@/components/luminous/SiteShell";
import { HomeLuminous } from "@/components/luminous/HomeLuminous";

type Language = "es" | "en";

const ES_URL = "https://luisroc.com/";
const EN_URL = "https://luisroc.com/en";

const meta = {
  es: {
    title: "Consultor SEO & Search Growth para EE. UU. | Luis ROC",
    description:
      "Consultoría SEO para empresas B2B, e-commerce y marcas que crecen en Estados Unidos: SEO técnico, internacional, local, Amazon y AI Search.",
    url: ES_URL,
    keywords: [
      "consultor SEO",
      "search growth consultant",
      "SEO técnico",
      "SEO internacional",
      "SEO bilingüe",
      "AI Search",
      "SEO para empresas B2B",
    ],
    summary:
      "Luis ROC es consultor SEO y Search Growth. Trabaja con empresas B2B, e-commerce y marcas que compiten en Estados Unidos en inglés y español, con foco en SEO técnico, investigación de demanda, contenido estratégico y medición de conversiones.",
    keyPoints: [
      "Consultoría SEO y Search Growth para el mercado de Estados Unidos",
      "Áreas: SEO técnico, internacional, local e hispano, e-commerce y Amazon, AI Search, estrategia e investigación",
      "ROC Search Growth Framework: research, base técnica, intención, autoridad, visibilidad, conversión y medición",
      "Trabajo bilingüe EN/ES con localización real, no traducción literal",
      "Participación estratégica directa del consultor en diagnóstico, prioridades y lectura de resultados",
    ],
    jobTitle: "Consultor SEO y Search Growth",
    personDescription:
      "Consultor SEO y Search Growth especializado en empresas B2B, e-commerce y marcas que compiten en Estados Unidos, con trabajo bilingüe inglés/español.",
  },
  en: {
    title: "SEO & Search Growth Consultant | Luis ROC",
    description:
      "SEO consulting for B2B companies, ecommerce brands and businesses growing in the U.S.: technical, international, local, Amazon and AI Search.",
    url: EN_URL,
    keywords: [
      "SEO consultant",
      "search growth consultant",
      "technical SEO",
      "international SEO",
      "bilingual SEO",
      "AI search visibility",
      "B2B SEO consulting",
    ],
    summary:
      "Luis ROC is an SEO and search growth consultant working with B2B companies, ecommerce businesses and brands competing in the United States in English and Spanish, focused on technical SEO, demand research, content strategy and conversion measurement.",
    keyPoints: [
      "SEO and search growth consulting for the United States market",
      "Areas: technical, international, local and Hispanic SEO, ecommerce and Amazon, AI Search, strategy and research",
      "ROC Search Growth Framework: research, technical foundation, intent, authority, visibility, conversion and measurement",
      "Bilingual EN/ES work based on localization, not literal translation",
      "Direct consultant involvement in diagnosis, prioritization and interpretation of results",
    ],
    jobTitle: "SEO & Search Growth Consultant",
    personDescription:
      "SEO and search growth consultant specialized in B2B companies, ecommerce businesses and brands competing in the United States, working in English and Spanish.",
  },
} as const;

const Index = () => {
  const { pathname } = useLocation();
  const language: Language = pathname.startsWith("/en") ? "en" : "es";
  const t = meta[language];

  // Fase 4: la Person y el WebSite se declaran una sola vez en el grafo global
  // (src/data/entity.ts). Aquí solo la WebPage de la home, referenciando por @id.
  const homePageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${t.url}#webpage`,
    name: t.title,
    description: t.description,
    url: t.url,
    inLanguage: language === "es" ? "es-US" : "en-US",
    isPartOf: WEBSITE_REF,
    about: PERSON_REF,
    publisher: ORG_REF,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: "https://luisroc.com/media/luis-roc/portrait.png",
    },
  };


  return (
    <SEOWrapper
      title={t.title}
      description={t.description}
      keywords={[...t.keywords]}
      url={t.url}
      esUrl={ES_URL}
      enUrl={EN_URL}
      language={language}
      pageType="website"
      localBusiness={false}
      summary={t.summary}
      keyPoints={[...t.keyPoints]}
    >
      <Helmet>
        <html lang={language === "es" ? "es" : "en"} />
        <script type="application/ld+json">{JSON.stringify(homePageSchema)}</script>
      </Helmet>

      <SiteShell language={language} altUrl={language === "es" ? "/en" : "/"}>
        <HomeLuminous language={language} />
      </SiteShell>
    </SEOWrapper>
  );
};

export default Index;
